import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Warning, Info, ArrowsClockwise } from '@phosphor-icons/react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ManagerAssistant } from '../components/ManagerAssistant';
import { PosReportUpload } from '../components/PosReportUpload';
import { PosSection, PosMappingAndContribution } from '../components/PosAnalytics';
import { RevenueDailyChart, CategoryBarChart, CategoryDailyChart, MonthlyChart, CoverageCard, ExcludedTable } from '../components/SmartCharts';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const lastDay = (y, m) => new Date(y, m + 1, 0).getDate();

const buildPreset = (key) => {
  const t = new Date();
  const y = t.getFullYear(), m = t.getMonth(), d = t.getDate();
  const py = m === 0 ? y - 1 : y, pm = m === 0 ? 11 : m - 1;
  switch (key) {
    case 'same_days':
      return [iso(new Date(py, pm, 1)), iso(new Date(py, pm, Math.min(d, lastDay(py, pm)))), iso(new Date(y, m, 1)), iso(t)];
    case 'first10':
      return [iso(new Date(py, pm, 1)), iso(new Date(py, pm, 10)), iso(new Date(y, m, 1)), iso(new Date(y, m, 10))];
    case 'months':
      return [iso(new Date(py, pm, 1)), iso(new Date(py, pm, lastDay(py, pm))), iso(new Date(y, m, 1)), iso(new Date(y, m, lastDay(y, m)))];
    case 'last3':
      return [iso(new Date(y, m - 5, 1)), iso(new Date(y, m - 2, 0)), iso(new Date(y, m - 2, 1)), iso(new Date(y, m + 1, 0))];
    case 'yearly':
      return [iso(new Date(y - 1, 0, 1)), iso(new Date(y - 1, 11, 31)), iso(new Date(y, 0, 1)), iso(new Date(y, 11, 31))];
    default:
      return null;
  }
};

const PRESETS = [
  ['same_days', 'Geçen ay / bu ay (aynı günler)'],
  ['first10', 'Geçen ayın 1–10 / bu ayın 1–10'],
  ['months', 'Geçen ay / bu ay'],
  ['last3', 'Önceki 3 ay / son 3 ay'],
  ['yearly', 'Geçen yıl / bu yıl'],
];

const money = (v) => (v === null || v === undefined ? '—' : formatCurrency(v));
const pctText = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`);
const ratioText = (v) => (v === null || v === undefined ? 'Güvenilmez' : `%${v.toFixed(2)}`);

const PctBadge = ({ value, inverse }) => {
  if (value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const bad = inverse ? value < 0 : value > 0;
  return <span className={bad ? 'text-red-600 font-medium' : 'text-emerald-600 font-medium'}>{pctText(value)}</span>;
};

const CompareTable = ({ title, rows, testid, ratioLabel = 'Satın alma / ciro' }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
    <CardContent className="overflow-x-auto">
      <Table className="text-sm">
        <TableHeader>
          <TableRow>
            <TableHead>Kategori</TableHead>
            <TableHead className="text-right">Önceki</TableHead>
            <TableHead className="text-right">Yeni</TableHead>
            <TableHead className="text-right">Fark</TableHead>
            <TableHead className="text-right">Değişim</TableHead>
            <TableHead className="text-right hidden md:table-cell">Günlük ort.</TableHead>
            <TableHead className="text-right">{ratioLabel}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(([key, r]) => (
            <TableRow key={key} data-testid={`${testid}-row-${key}`}>
              <TableCell className="font-medium">{r.label}</TableCell>
              <TableCell className="text-right">{money(r.a_total)}</TableCell>
              <TableCell className="text-right">{money(r.b_total)}</TableCell>
              <TableCell className="text-right">{money(r.diff)}</TableCell>
              <TableCell className="text-right"><PctBadge value={r.pct} /></TableCell>
              <TableCell className="text-right hidden md:table-cell">{money(r.a_daily_avg)} → {money(r.b_daily_avg)}</TableCell>
              <TableCell className="text-right text-xs">{ratioText(r.a_ratio_to_revenue)} → {ratioText(r.b_ratio_to_revenue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const RevenueCard = ({ rev, A, B }) => (
  <Card data-testid="sa-revenue-card">
    <CardHeader className="pb-2"><CardTitle className="text-base">ERP Ciro (daily_sales)</CardTitle></CardHeader>
    <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><p className="text-muted-foreground">Önceki dönem (ERP günlük ciro)</p><p className="text-lg font-semibold" data-testid="sa-rev-a">{money(rev.a_total)}</p><p className="text-xs">Kapsama %{rev.a_coverage_pct} ({A.revenue.days_with_data}/{A.days} gün)</p></div>
      <div><p className="text-muted-foreground">Yeni dönem (ERP günlük ciro)</p><p className="text-lg font-semibold" data-testid="sa-rev-b">{money(rev.b_total)}</p><p className="text-xs">Kapsama %{rev.b_coverage_pct} ({B.revenue.days_with_data}/{B.days} gün)</p></div>
      <div><p className="text-muted-foreground">Değişim</p><p className="text-lg" data-testid="sa-rev-pct">{rev.reliable ? <PctBadge value={rev.pct} inverse /> : <Badge variant="outline">Güvenilmez – eksik gün</Badge>}</p></div>
      <div><p className="text-muted-foreground">Günlük ortalama (kayıtlı günler)</p><p>{money(rev.a_daily_avg)} → {money(rev.b_daily_avg)}</p><PctBadge value={rev.daily_avg_pct} inverse /></div>
    </CardContent>
  </Card>
);

const AlertList = ({ items, testid, title }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2"><CardTitle className="text-base">{title} ({items.length})</CardTitle></CardHeader>
    <CardContent className="space-y-1 max-h-72 overflow-y-auto text-sm">
      {items.length === 0 && <p className="text-muted-foreground">Kayıt yok</p>}
      {items.map((a, i) => (
        <div key={i} className="flex gap-2 items-start">
          {a.level === 'warning' ? <Warning className="text-amber-600 shrink-0 mt-0.5" size={16} /> : <Info className="text-blue-600 shrink-0 mt-0.5" size={16} />}
          <span>{a.message}</span>
        </div>
      ))}
    </CardContent>
  </Card>
);

const ControlList = ({ items }) => (
  <Card data-testid="sa-control-required">
    <CardHeader className="pb-2"><CardTitle className="text-base">Kontrol Gerekli – Et/Tavuk kayıtları ({items.length})</CardTitle></CardHeader>
    <CardContent className="overflow-x-auto max-h-80 overflow-y-auto">
      <Table className="text-sm">
        <TableHeader><TableRow><TableHead>Tarih</TableHead><TableHead>Ürün</TableHead><TableHead>Kayıt türü</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead>Neden</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((c) => (
            <TableRow key={c.id}><TableCell>{c.date}</TableCell><TableCell>{c.product_name}</TableCell><TableCell>{c.record_category}</TableCell><TableCell className="text-right">{money(c.total_amount)}</TableCell><TableCell className="text-xs">{c.reason}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-muted-foreground mt-2">Kayıtlar değiştirilmedi; analizde ürün adına göre sınıflandırıldı.</p>
    </CardContent>
  </Card>
);

const NoAccess = () => (
  <Layout>
    <div className="p-8" data-testid="sa-no-access">
      <h1 className="text-2xl font-heading font-bold">Akıllı Analiz</h1>
      <p className="mt-4 text-muted-foreground">Bu sayfaya erişim yetkiniz yok. Yönetici tarafından "Akıllı Analiz" yetkisi verilmesi gerekir.</p>
    </div>
  </Layout>
);

export default function SmartAnalytics() {
  const { hasPermission, permissionsLoading } = useAuth();
  const [range, setRange] = useState(buildPreset('first10'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const allowed = hasPermission('smart_analytics', 'view');

  const load = async (r = range) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/smart-analytics/compare', { params: { a_start: r[0], a_end: r[1], b_start: r[2], b_end: r[3] }, timeout: 90000 });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Analiz yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (allowed) load(); }, [allowed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return permissionsLoading ? <Layout><div className="p-8">Yükleniyor...</div></Layout> : <NoAccess />;

  const setField = (i, v) => setRange(range.map((x, j) => (j === i ? v : x)));
  const applyPreset = (k) => { const r = buildPreset(k); setRange(r); load(r); };
  const warnings = data ? [...data.period_a.warnings.map((w) => ({ ...w, message: `Önceki: ${w.message}` })), ...data.period_b.warnings.map((w) => ({ ...w, message: `Yeni: ${w.message}` }))] : [];
  const control = data ? [...data.period_a.control_required, ...data.period_b.control_required] : [];
  const excluded = data ? [...data.period_a.excluded_expenses, ...data.period_b.excluded_expenses] : [];

  return (
    <Layout>
      <div className="space-y-6 p-4 md:p-8" data-testid="smart-analytics-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Akıllı Analiz</h1>
          <p className="text-sm text-muted-foreground mt-1">Gerçek ERP kayıtlarından dönem karşılaştırması. Rakamlar tahmin edilmez.</p>
        </div>

        <Tabs defaultValue="analysis" className="space-y-6">
          <TabsList>
            <TabsTrigger value="analysis" data-testid="sa-tab-analysis">Analiz</TabsTrigger>
            <TabsTrigger value="assistant" data-testid="sa-tab-assistant">Yönetici Asistanı</TabsTrigger>
          </TabsList>
          <TabsContent value="assistant"><ManagerAssistant /></TabsContent>
          <TabsContent value="analysis" className="space-y-6">
        <PosReportUpload />

        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-wrap gap-2">
              {PRESETS.map(([k, label]) => (
                <Button key={k} size="sm" variant="outline" onClick={() => applyPreset(k)} data-testid={`sa-preset-${k}`}>{label}</Button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              {['Önceki başlangıç', 'Önceki bitiş', 'Yeni başlangıç', 'Yeni bitiş'].map((l, i) => (
                <div key={l} className="space-y-1">
                  <Label className="text-xs">{l}</Label>
                  <Input type="date" value={range[i]} onChange={(e) => setField(i, e.target.value)} data-testid={`sa-date-${i}`} />
                </div>
              ))}
              <Button onClick={() => load()} disabled={loading} data-testid="sa-compare-btn">
                <ArrowsClockwise size={16} className={loading ? 'animate-spin mr-2' : 'mr-2'} /> Karşılaştır
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="p-4 border border-destructive rounded-lg text-destructive text-sm flex items-center gap-3" data-testid="sa-error">
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={() => load()} data-testid="sa-retry-btn">Yeniden Dene</Button>
          </div>
        )}
        {loading && !data && <p className="text-muted-foreground" data-testid="sa-loading">Analiz hesaplanıyor...</p>}

        {data && (
          <>
            <RevenueCard rev={data.revenue} A={data.period_a} B={data.period_b} />
            <PosSection data={data} />
            <CoverageCard data={data} />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <RevenueDailyChart data={data} />
              <CategoryBarChart title="Satın alma kategorileri (önceki / yeni)" entries={Object.entries(data.purchases)} testid="sa-chart-purchases" />
              <CategoryDailyChart data={data} options={[...Object.entries(data.purchases).map(([k, r]) => [k, r.label]), ...Object.entries(data.expenses).map(([k, r]) => [`exp:${k}`, `Gider: ${r.label}`])]} />
              <CategoryBarChart title="Gider kategorileri (önceki / yeni)" entries={Object.entries(data.expenses)} testid="sa-chart-expenses" />
            </div>
            <MonthlyChart data={data} />
            <PosMappingAndContribution start={data.period_b.start} end={data.period_b.end} />
            <CompareTable title="Satın Almalar (kategoriler ayrık)" rows={[...Object.entries(data.purchases), ['__total', { ...data.totals.purchases, label: 'TOPLAM SATIN ALMA' }]]} testid="sa-purchases" />
            <CompareTable title="Muhasebe Giderleri" rows={[...Object.entries(data.expenses), ['__total', { ...data.totals.expenses, label: 'TOPLAM GİDER' }]]} testid="sa-expenses" ratioLabel="Gider / ciro" />
            <CompareTable title="PDF Fatura Alımları (ayrı – toplama eklenmedi)" rows={[['pdf', { ...data.totals.pdf_invoices_separate, label: 'PDF fatura kalemleri' }]]} testid="sa-pdf" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AlertList title="Akıllı Uyarılar" items={data.alerts} testid="sa-alerts" />
              <AlertList title="Veri Kapsama ve Mükerrer Uyarıları" items={warnings} testid="sa-data-warnings" />
            </div>
            <ControlList items={control} />
            <ExcludedTable items={excluded} />
            <ul className="text-xs text-muted-foreground list-disc pl-5" data-testid="sa-notes">
              {data.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </>
        )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
